import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Banner,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularLoader,
  ColumnsIcon,
  OpenInNewIcon,
  RefreshIcon,
  useDocumentTitle,
} from '@forge/common';
import { config } from '@onering/npm-package/web';
import type { InfraInstance, ServiceStatus } from '@/api/observability';
import { useInfraInstances } from '@/hooks/useInfraInstances';

const grafanaBaseUrl = config('GRAFANA_URL') ?? '';

const statusMeta: Record<
  ServiceStatus,
  { label: string; core: string; badge: 'success' | 'warning' | 'error' | 'neutral' }
> = {
  operational: { label: 'Connected', core: 'bg-green-500', badge: 'success' },
  degraded: { label: 'Degraded', core: 'bg-yellow-500', badge: 'warning' },
  down: { label: 'Unreachable', core: 'bg-red-500', badge: 'error' },
  unknown: { label: 'Unknown', core: 'bg-gray-400', badge: 'neutral' },
};

/**
 * Deep-links the tier's Grafana Infrastructure Health dashboard, filtered to a
 * single database via its template variable. Empty when no dashboard/Grafana
 * URL is configured, in which case the button is rendered disabled.
 */
const dashboardHref = (
  dashboardUid: string | null,
  dashboardVar: string | null,
  database: string,
): string => {
  if (!grafanaBaseUrl || !dashboardUid) return '';
  const varParam = dashboardVar
    ? `?var-${dashboardVar}=${encodeURIComponent(database)}`
    : '';
  return `${grafanaBaseUrl}/d/${dashboardUid}/infrastructure-health${varParam}`;
};

const DatabaseCard = ({
  instance,
  href,
}: {
  instance: InfraInstance;
  href: string;
}) => {
  const meta = statusMeta[instance.status];
  return (
    <Card className="h-full">
      <CardContent className="flex h-full flex-col gap-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-color-secondary dark:bg-gray-800 dark:text-color-secondary-dark">
              <ColumnsIcon />
            </span>
            <div className="min-w-0">
              <p className="truncate font-semibold">{instance.name}</p>
              <p className="mt-0.5 text-xs text-color-tertiary">Oracle database</p>
            </div>
          </div>
          <span className="flex shrink-0 items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-full ${meta.core}`} aria-hidden="true" />
            <Badge type={meta.badge}>{meta.label}</Badge>
          </span>
        </div>

        <p className="min-h-[1.25rem] text-sm text-color-tertiary">
          {instance.status === 'down' && instance.reason
            ? instance.reason
            : instance.status === 'operational'
              ? 'Exporter reports the database is answering connections.'
              : 'No connectivity metric reported for this database.'}
        </p>

        <div className="mt-auto flex items-center justify-end border-t border-gray-200 pt-3 dark:border-gray-800">
          {href ? (
            <Button
              as="link"
              href={href}
              variant="quiet-primary"
              icon={<OpenInNewIcon />}
              target="_blank"
              rel="noopener noreferrer"
              external
            >
              View dashboard
            </Button>
          ) : (
            <Button variant="quiet-primary" icon={<OpenInNewIcon />} disabled>
              View dashboard
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const OracleDatabasesPage = () => {
  useDocumentTitle('Oracle Databases — OneRing Portal');
  const navigate = useNavigate();
  const { data, isLoading, error, reload } = useInfraInstances('oracle');

  const instances = data?.instances ?? [];
  const connected = instances.filter((i) => i.status === 'operational').length;

  return (
    <div className="space-y-6 p-4">
      <Breadcrumbs
        crumbs={[
          { id: 'observability', label: 'Observability', href: '/observability' },
          { id: 'oracle', label: 'Oracle Databases', noAction: true },
        ]}
        onChange={({ href }) => {
          if (href) navigate(href);
        }}
      />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold">Oracle Databases</h2>
          <p className="mt-1 max-w-2xl text-sm text-color-tertiary">
            Live connectivity for every Oracle database monitored in this environment, derived from
            the Oracle exporter. Open a database&rsquo;s Grafana dashboard for deeper metrics.
          </p>
        </div>
        <Button
          variant="secondary"
          icon={<RefreshIcon />}
          onClick={() => reload()}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </div>

      {error && !data && (
        <Banner
          variant="inline"
          type="error"
          title="Failed to load Oracle databases"
          description={error}
          canClose={false}
        />
      )}

      {data && !data.metricsConfigured && (
        <Banner
          variant="inline"
          type="warning"
          title="Metrics not configured"
          description="This portal has no Prometheus URL configured, so database connectivity cannot be shown."
          canClose={false}
        />
      )}

      {isLoading && !data ? (
        <div className="flex min-h-[320px] flex-col items-center justify-center gap-3">
          <CircularLoader size="large" />
          <p className="text-sm text-color-tertiary">Loading Oracle databases…</p>
        </div>
      ) : instances.length === 0 ? (
        <Card>
          <CardContent>
            <p className="text-sm text-color-tertiary">
              No Oracle databases are reporting metrics in this environment yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-sm text-color-tertiary">
            <span className="font-semibold tabular-nums">{connected}</span> of{' '}
            <span className="font-semibold tabular-nums">{instances.length}</span> databases
            connected
          </p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {instances.map((instance) => (
              <DatabaseCard
                key={instance.id}
                instance={instance}
                href={dashboardHref(data?.dashboardUid ?? null, data?.dashboardVar ?? null, instance.name)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default OracleDatabasesPage;
